import { RESOLVER_CONFLITO_LABEL } from "@/shared/components/gantt-carga/gantt-carga.constants";
import type { MenuItem } from "primereact/menuitem";
import type { MouseEvent } from "react";

import { GANTT_LAYOUT_PRESETS } from "@/ds";
import type { GanttLayout } from "@/ds";
import { marcaSaidaKey } from "@/modules/recursos/simulacao";
import type { MarcaSimulacao, MarcasSimulacao } from "@/modules/recursos/simulacao";
import { lanesMinHeightRem } from "@/shared/components/gantt";

import type { ObraAlocacaoRow } from "./carga-fornecedor-gantt-rows";
import type { ConflictDetail } from "./components/conflict-detail-dialog";
import type { GanttItem, GanttRowModel } from "@/shared/components/gantt-carga/gantt-rows";

/** Linha do Gantt sem o que só a Carga de Profissionais usa para resolver conflito (HU 2434). */
export type GanttRowView = Omit<GanttRowModel, "conflitos" | "profissional">;

export const COMFORTABLE = GANTT_LAYOUT_PRESETS.comfortable;

/** Fornecedor (compacto): barras de 1,25 com 0,3 entre as faixas e 0,6 de respiro, como o Gantt de Profissionais. */
export const SEGMENT_LAYOUT: Partial<GanttLayout> = {
  ...COMFORTABLE,
  barHeightRem: 1.25,
  laneHeightRem: 1.25 + 0.3,
  rowPaddingRem: 0.6,
};
const SEGMENT_ROW_MIN_HEIGHT_REM = 2.5;
/** O respiro de cima e o de baixo (0,6 cada), menos o vão que não existe depois da última faixa (0,3). */
const SEGMENT_ROW_PADDING_REM = 2 * 0.6 - 0.3;

/**
 * Obra (linha, Figma): datas e "Obra" acima da linha e, abaixo, o pill de duração. A faixa começa a 0,5 do topo da
 * linha da tabela; o `LineMark` posiciona pela linha, que fica 1 abaixo (o rótulo de 0,75 e o vão de 0,25). A linha tem
 * 4px (0,25rem), 2px (0,125rem) a mais que os 2px do DS, e o pill desce os mesmos 0,125rem: 0,4375 → 0,5625 abaixo do
 * topo da linha.
 */
export const LINE_ROW_MIN_HEIGHT_REM = 3.8;
export const LINE_ROW_PADDING_REM = 0.5;
export const LINE_HEAD_REM = 1;
export const LINE_LAYOUT: Partial<GanttLayout> = {
  ...COMFORTABLE,
  rowPaddingRem: LINE_ROW_PADDING_REM + LINE_HEAD_REM,
};
export const PILL_LAYOUT: Partial<GanttLayout> = { ...LINE_LAYOUT, rowPaddingRem: 2.0625 };

/**
 * Marca "Alterado na simulação" da barra. As linhas da visão por Obra compõem a chave com o
 * escopo macro (`<alocação>-<escopo>`), então vale também o prefixo da alocação.
 */
export function marcaDaBarra(marcas: MarcasSimulacao, key: string): MarcaSimulacao | undefined {
  const exata = marcas.get(key);
  if (exata) return exata;
  for (const [alocacaoId, marca] of marcas) {
    if (key.startsWith(`${alocacaoId}-`)) return marca;
  }
  return undefined;
}

export function marcaDaLinha(
  marcas: MarcasSimulacao,
  row: { key: string; items: GanttItem[] },
): MarcaSimulacao | undefined {
  // Quem entrou tem a barra marcada; quem saiu (sem a barra) é marcado pela linha.
  return (
    row.items.map((item) => marcaDaBarra(marcas, item.key)).find(Boolean) ??
    marcas.get(marcaSaidaKey(row.key))
  );
}

/** Recebe os conflitos a mostrar e o evento do mouse, que posiciona o tooltip. */
export type ConflictHoverHandler = (conflicts: ConflictDetail, event: MouseEvent) => void;

export function segmentRowMinHeight(lanes: number): string {
  return `${lanesMinHeightRem({
    lanes,
    laneHeightRem: SEGMENT_LAYOUT.laneHeightRem ?? 0,
    paddingRem: SEGMENT_ROW_PADDING_REM,
    minRem: SEGMENT_ROW_MIN_HEIGHT_REM,
  })}rem`;
}

export function lineRowMinHeight(lanes: number): string {
  return `${lanesMinHeightRem({
    lanes,
    laneHeightRem: COMFORTABLE.laneHeightRem,
    paddingRem: 2 * LINE_ROW_PADDING_REM,
    minRem: LINE_ROW_MIN_HEIGHT_REM,
  })}rem`;
}

/** Menu ⋮ da linha: "Resolver Conflito" só com conflito em aberto; sem conflito, não há menu. */
export function rowActions(temConflito: boolean, resolver?: () => void): MenuItem[] | undefined {
  if (!temConflito || !resolver) return undefined;
  return [{ label: RESOLVER_CONFLITO_LABEL, icon: "pi pi-thumbs-up", command: resolver }];
}

export interface ObraGroupRows {
  key: string;
  obra: ObraAlocacaoRow["obra"];
  rows: ObraAlocacaoRow[];
}

/** Junta as linhas (uma por alocação) de cada obra; a primeira de cada obra abre o grupo. */
export function groupByObra(rows: ObraAlocacaoRow[]): ObraGroupRows[] {
  const groups: ObraGroupRows[] = [];
  for (const row of rows) {
    const current = groups.at(-1);
    if (row.firstOfObra || !current) groups.push({ key: row.key, obra: row.obra, rows: [row] });
    else current.rows.push(row);
  }
  return groups;
}

/** Barra da obra recolhida: do início da primeira alocação ao fim da última. */
export function summaryItem(group: ObraGroupRows): GanttItem | null {
  const items = group.rows.flatMap((row) => row.items);
  const [first] = items;
  if (!first) return null;
  const startItem = items.reduce((a, b) => (b.left < a.left ? b : a));
  const endItem = items.reduce((a, b) => (b.left + b.width > a.left + a.width ? b : a));
  return {
    ...first,
    key: `${group.key}-resumo`,
    lane: 0,
    left: startItem.left,
    width: endItem.left + endItem.width - startItem.left,
    start: startItem.start,
    end: endItem.end,
    duration: undefined,
    // Recolhida, os escopos não aparecem: sem pílula de duração da obra.
    pill: undefined,
    label: group.obra.rotulo.sigla,
    rotulo: group.obra.rotulo,
    past: items.every((item) => item.past),
  };
}

/** Conflito em aberto: o mouse sobre a faixa mostra o detalhe; o tratado (verde) não tem detalhe. */
export function hoverDaFaixa(
  band: { info?: ConflictDetail[number]; ignored?: boolean },
  onConflictHover?: ConflictHoverHandler,
) {
  return (event: MouseEvent) => {
    if (band.info && !band.ignored) onConflictHover?.([band.info], event);
  };
}
